import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { Polygon } from 'geojson';
import { ReactNode } from 'react';
import ExploreActionType from '@alltrails/amplitude/enums/ExploreActionType';
import FeedbackProvided from '@alltrails/amplitude/enums/FeedbackProvided';
import type SearchOrigin from '@alltrails/amplitude/enums/SearchOrigin';
import SearchResultSelectionMethod from '@alltrails/amplitude/enums/SearchResultSelectionMethod';
import SearchSuggestionSelectionType from '@alltrails/amplitude/enums/SearchSuggestionSelectionType';
import type { AreaSearchResult, PlaceSearchResult, POISearchResult, StreetAddressResult } from '@alltrails/types/AlgoliaResults';
import AdminExploreFilters from '../types/AdminExploreFilters';
import type BoundingBox from '../types/BoundingBox';
import DistanceAway from '../types/DistanceAway';
import type ExploreServiceFilters from '../types/ExploreServiceFilters';
import { ExploreServiceSearchResult, ExploreServiceSearchResultsSummary } from '../types/ExploreServiceResponse';
import type ExploreServiceSort from '../types/ExploreServiceSort';
import TrailEngagement from '../types/TrailEngagement';
import { defaultExploreServiceSort } from '../utils/constants';
import type { SeasonalMonth } from '../utils/seasonalMonth';
import { getCurrentMonth, monthToApiParam, sortParamToMonth } from '../utils/seasonalMonth';
import { exploreServiceApi } from './exploreServiceApi';

export type LocationResult = AreaSearchResult | PlaceSearchResult | POISearchResult | StreetAddressResult;

export type NaturalLanguageSearchAnalyticData = {
  clickPosition?: number;
  queryId?: string;
  queryText?: string;
  selectionType?: SearchSuggestionSelectionType;
  selectionMethod?: SearchResultSelectionMethod;
  searchOrigin?: SearchOrigin;
};

export type ExploreServiceState = {
  adminFilters: AdminExploreFilters;
  allFiltersModalOpen: boolean;
  boundingBox: BoundingBox | undefined;
  customSortOptions?: { label: ReactNode; value: string }[];
  customSortValue?: string;
  distanceAway: DistanceAway | undefined;
  distanceAwayIsochrone: Polygon | undefined;
  distanceAwayLoading?: boolean;
  // Just used to keep track of the most recent action type for logging analytics events.
  // This isn't a perfect solution but its the best we can do without a major overhaul to the explore-service integration.
  exploreActionType?: ExploreActionType;
  filters: ExploreServiceFilters;
  listQuery?: string;
  locationResult?: LocationResult;
  mapRotation: number;
  naturalLanguageQuery?: string;
  naturalLanguageSearchAnalyticData?: NaturalLanguageSearchAnalyticData;
  naturalLanguageSearchFeedback?: FeedbackProvided;
  naturalLanguageSearchIsActive?: boolean;
  naturalLanguageSearchSummary?: string;
  searchIsLoading?: boolean;
  searchResults: ExploreServiceSearchResult[];
  searchResultsPlace?: AreaSearchResult | PlaceSearchResult | POISearchResult;
  searchResultsSummary?: ExploreServiceSearchResultsSummary;
  seasonalMonth?: SeasonalMonth;
  skipQuery: boolean;
  sort: ExploreServiceSort;
  trailEngagement: TrailEngagement[];
  userLocation?: { lat: number; lng: number };
};

export const initialState: ExploreServiceState = {
  adminFilters: {},
  allFiltersModalOpen: false,
  boundingBox: undefined,
  distanceAway: undefined,
  distanceAwayIsochrone: undefined,
  distanceAwayLoading: false,
  exploreActionType: undefined,
  filters: {},
  listQuery: undefined,
  mapRotation: 0,
  naturalLanguageQuery: undefined,
  naturalLanguageSearchFeedback: undefined,
  naturalLanguageSearchIsActive: false,
  naturalLanguageSearchSummary: undefined,
  searchResults: [],
  seasonalMonth: getCurrentMonth(),
  skipQuery: false,
  sort: defaultExploreServiceSort,
  trailEngagement: []
};

const exploreServiceSlice = createSlice({
  name: 'exploreService',
  initialState,
  reducers: {
    resetState: (state, action: PayloadAction<{ persistKeys?: (keyof ExploreServiceState)[]; initialState?: Partial<ExploreServiceState> }>) => {
      const defaultState: ExploreServiceState = { ...initialState, ...(action.payload.initialState || {}) };
      action.payload.persistKeys?.forEach(key => {
        const persistValue = state[key];
        (defaultState[key] as typeof persistValue) = persistValue;
      });

      return defaultState;
    },
    clearNaturalLanguageSearchAndFilters: state => {
      const keysToClear = [
        'adminFilters',
        'distanceAway',
        'distanceAwayIsochrone',
        'filters',
        'locationResult',
        'naturalLanguageQuery',
        'naturalLanguageSearchAnalyticData',
        'naturalLanguageSearchFeedback',
        'naturalLanguageSearchIsActive',
        'naturalLanguageSearchSummary',
        'trailEngagement'
      ];
      keysToClear.forEach(key => {
        state[key] = initialState[key];
      });
    },
    updateAdminFilters: (state, action: PayloadAction<AdminExploreFilters>) => {
      state.adminFilters = action.payload;
    },
    updateAllFiltersModalOpen: (state, action: PayloadAction<boolean>) => {
      state.allFiltersModalOpen = action.payload;
    },
    updateBoundingBox: (state, action: PayloadAction<BoundingBox | undefined>) => {
      state.boundingBox = action.payload;
    },
    updateCustomSortValue: (state, action: PayloadAction<string | undefined>) => {
      state.customSortValue = action.payload;
    },
    updateDistanceAway: (state, action: PayloadAction<DistanceAway | undefined>) => {
      state.distanceAway = action.payload;
    },
    updateDistanceAwayIsochrone: (state, action: PayloadAction<Polygon | undefined>) => {
      state.distanceAwayIsochrone = action.payload;
    },
    updateDistanceAwayLoading: (state, action: PayloadAction<boolean>) => {
      state.distanceAwayLoading = action.payload;
    },
    updateExploreActionType: (state, action: PayloadAction<ExploreActionType>) => {
      state.exploreActionType = action.payload;
    },
    updateFilters: (state, action: PayloadAction<ExploreServiceFilters>) => {
      state.filters = action.payload;
    },
    updateListQuery: (state, action: PayloadAction<string | undefined>) => {
      state.listQuery = action.payload || undefined;
    },
    updateLocationResult: (state, action: PayloadAction<LocationResult | undefined>) => {
      state.locationResult = action.payload;
    },
    updateMapRotation: (state, action: PayloadAction<number>) => {
      state.mapRotation = action.payload;
    },
    updateNaturalLanguageQuery: (state, action: PayloadAction<string | undefined>) => {
      state.naturalLanguageSearchFeedback = undefined; // Clear the feedback when the query changes
      state.naturalLanguageSearchSummary = undefined; // Clear the result explanation when the query changes
      state.naturalLanguageQuery = action.payload;

      if (!action.payload) {
        state.naturalLanguageSearchAnalyticData = undefined; // Clear the analytic data when the query is cleared
        state.naturalLanguageSearchIsActive = false; // Clear the active state when the query is cleared
      }
    },
    updateNaturalLanguageSearchAnalyticData: (state, action: PayloadAction<NaturalLanguageSearchAnalyticData | undefined>) => {
      state.naturalLanguageSearchAnalyticData = action.payload;
    },
    updateNaturalLanguageSearchFeedback: (state, action: PayloadAction<FeedbackProvided | undefined>) => {
      state.naturalLanguageSearchFeedback = action.payload;
    },
    updateNaturalLanguageSearchIsActive: (state, action: PayloadAction<boolean>) => {
      state.naturalLanguageSearchIsActive = action.payload;
    },
    updateNaturalLanguageSearchSummary: (state, action: PayloadAction<string | undefined>) => {
      state.naturalLanguageSearchSummary = action.payload;
    },
    updateSearchIsLoading: (state, action: PayloadAction<boolean>) => {
      state.searchIsLoading = action.payload;
    },
    updateSearchResults: (state, action: PayloadAction<ExploreServiceSearchResult[]>) => {
      state.searchResults = action.payload;
    },
    updateSearchResultsPlace: (state, action: PayloadAction<AreaSearchResult | PlaceSearchResult | POISearchResult | undefined>) => {
      state.searchResultsPlace = action.payload;
    },
    updateSearchResultsSummary: (state, action: PayloadAction<ExploreServiceSearchResultsSummary | undefined>) => {
      state.searchResultsSummary = action.payload;
    },
    updateSkipQuery: (state, action: PayloadAction<boolean>) => {
      state.skipQuery = action.payload;
    },
    updateSort: (state, action: PayloadAction<ExploreServiceSort>) => {
      state.sort = action.payload;
      // If switching to seasonal or a month, sync the seasonalMonth
      if (action.payload && (action.payload === 'seasonal' || action.payload.startsWith('month_'))) {
        state.seasonalMonth = sortParamToMonth(action.payload);
      }
      // When switching away from seasonal, preserve the last selection (don't clear it)
    },
    updateSeasonalMonth: (state, action: PayloadAction<SeasonalMonth>) => {
      state.seasonalMonth = action.payload;
      // Also update sort to the corresponding month parameter
      // Use monthToApiParam which returns 'seasonal' for current month, 'month_X' for others
      state.sort = monthToApiParam(action.payload) as ExploreServiceSort;
    },
    updateTrailEngagement: (state, action: PayloadAction<TrailEngagement[]>) => {
      state.trailEngagement = action.payload;
    },
    updateUserLocation: (state, action: PayloadAction<{ lat: number; lng: number } | undefined>) => {
      state.userLocation = action.payload;
    }
  },
  extraReducers: builder => {
    builder.addMatcher(exploreServiceApi.endpoints.getResultsBoundingBox.matchFulfilled, (state, { payload }) => {
      if (payload.boundingBox) {
        state.boundingBox = payload.boundingBox;
        state.mapRotation = 0;
      }
    });
  }
});

export const {
  resetState,
  clearNaturalLanguageSearchAndFilters,
  updateAdminFilters,
  updateAllFiltersModalOpen,
  updateBoundingBox,
  updateCustomSortValue,
  updateDistanceAway,
  updateDistanceAwayIsochrone,
  updateDistanceAwayLoading,
  updateExploreActionType,
  updateFilters,
  updateListQuery,
  updateLocationResult,
  updateMapRotation,
  updateNaturalLanguageQuery,
  updateNaturalLanguageSearchAnalyticData,
  updateNaturalLanguageSearchFeedback,
  updateNaturalLanguageSearchIsActive,
  updateNaturalLanguageSearchSummary,
  updateSearchIsLoading,
  updateSearchResults,
  updateSearchResultsPlace,
  updateSearchResultsSummary,
  updateSeasonalMonth,
  updateSkipQuery,
  updateSort,
  updateTrailEngagement,
  updateUserLocation
} = exploreServiceSlice.actions;

export const exploreServiceReducer = exploreServiceSlice.reducer;
